import React, { useEffect, useMemo, useState } from "react";
import {
  ref,
  set,
  push,
  onValue,
  off,
  update,
  remove,
} from "firebase/database";
import { database } from "../firebase";
import {
  TextField,
  MenuItem,
  Button,
  Typography,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";

import "./scheduler.scss";

const SENSOR_OPTIONS = [
  { id: 0, label: "Accelerometer" },
  { id: 1, label: "ECG" },
  { id: 2, label: "Skin Temperature" },
  { id: 3, label: "Heart Rate" },
  { id: 4, label: "SPO2" },
  { id: 5, label: "PPG" },
  { id: 7, label: "Electrodermal Activity" },
];

// Convert YYYY-MM-DD -> timestamp (UTC midnight)
const dateToTimestamp = (dateStr) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return Date.UTC(year, month - 1, day); // midnight UTC
};

// Convert timestamp -> YYYY-MM-DD (local display)
const timestampToDate = (ts) => {
  const date = new Date(ts);
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const SchedulerComponent = ({ watchId, onClose }) => {
  const [sensor, setSensor] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [daysOfWeek, setDaysOfWeek] = useState([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ]); // 7 days of the week, Monday to Sunday

  const [showExisting, setShowExisting] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const schedulesRef = useMemo(
    () => ref(database, `Health/${watchId}/schedules`),
    [watchId]
  );

  useEffect(() => {
    if (!showExisting) return;
    const handler = (snap) => {
      const val = snap.val() || {};
      const list = Object.entries(val).map(([id, v]) => ({
        id,
        ...v,
        startDate: timestampToDate(v.startTimestamp),
        endDate: timestampToDate(v.endTimestamp),
      }));
      list.sort((a, b) =>
        `${a.startDate} ${a.time}`.localeCompare(`${b.startDate} ${b.time}`)
      );
      setSchedules(list);
    };
    onValue(schedulesRef, handler);
    return () => off(schedulesRef, "value", handler);
  }, [showExisting, schedulesRef]);

  const resetForm = () => {
    setSensor("");
    setStartDate("");
    setEndDate("");
    setTime("");
    setDaysOfWeek([false, false, false, false, false, false, false]); // Reset days selection
    setSelectedId(null);
  };

  const loadForEdit = (item) => {
    setSensor(item.sensor);
    setStartDate(item.startDate);
    setEndDate(item.endDate);
    setTime(item.time);
    setDaysOfWeek(
      item.daysOfWeek || [false, false, false, false, false, false, false]
    );
    setSelectedId(item.id);
  };

  const handleSave = async () => {
    if (sensor === "" || !startDate || !endDate || !time) {
      alert("Please fill out all fields.");
      return;
    }
    if (endDate < startDate) {
      alert("End date cannot be before start date.");
      return;
    }

       // Check if any days of the week are selected
    if (!daysOfWeek.some((day) => day)) {
      alert("Please select at least one day of the week.");
      return;
    }

    const startTs = dateToTimestamp(startDate);
    const endTs = dateToTimestamp(endDate);

    // Save the daysOfWeek as an array
    const frequency = daysOfWeek
      .map((selected, index) => (selected ? index + 1 : null))
      .filter((day) => day);

    setBusy(true);
    try {
      await set(ref(database, `Health/${watchId}/onDemandSensor`), {
        type: Number(sensor),
      });

      if (selectedId) {
        await update(
          ref(database, `Health/${watchId}/schedules/${selectedId}`),
          {
            sensor: Number(sensor),
            startTimestamp: startTs,
            endTimestamp: endTs,
            time,
            daysOfWeek: frequency, // Save days as frequency
          }
        );
      } else {
        const newRef = push(ref(database, `Health/${watchId}/schedules`));
        await set(newRef, {
          sensor: Number(sensor),
          startTimestamp: startTs,
          endTimestamp: endTs,
          time,
          daysOfWeek: frequency, // Save days as frequency
        });
      }

      if (!showExisting) setShowExisting(true);
      resetForm();
      alert("Schedule saved.");
    } catch (e) {
      console.error(e);
      alert("Failed to save schedule.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm("Delete this schedule?")) return;
    setBusy(true);
    try {
      await remove(ref(database, `Health/${watchId}/schedules/${selectedId}`));
      resetForm();
      alert("Schedule deleted.");
    } catch (e) {
      console.error(e);
      alert("Failed to delete schedule.");
    } finally {
      setBusy(false);
    }
  };

  // Map the daysOfWeek array (true/false) to day labels
  const mapDaysToLabels = (daysArray) => {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return daysArray
      .map((isSelected, index) => (isSelected ? days[index] : null))
      .filter((day) => day)
      .join(", ");
  };

  return (
    <div className="scheduler-shell">
      {/* Header with floating X */}
      <div className="scheduler-header">
        <div className="scheduler-title">
          <div className="icon-chip">
            <CalendarTodayIcon fontSize="small" />
          </div>
          <div>
            <p className="eyebrow">Automated pulls</p>
            <h3>Schedule data collections</h3>
            <div className="header-meta">
              <Chip
                size="small"
                variant="outlined"
                icon={<WatchLaterIcon />}
                label={`Watch • ${watchId || "unassigned"}`}
              />
            </div>
          </div>
        </div>

        <div className="header-actions">
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => setShowExisting((s) => !s)}
          >
            {showExisting ? "Hide saved" : "View saved"}
          </Button>
          {onClose && (
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="close"
              
            >
              <CloseIcon />
            </IconButton>
          )}
        </div>
      </div>

      <div className="scheduler-grid">
        {/* Form */}
        <div className="scheduler-card">
          <div className="card-head">
            <div className="icon-chip hollow">
              <ScheduleIcon />
            </div>
            <div>
              <h4>When should we pull?</h4>
              <p className="muted">Choose the metric and timing for automatic requests.</p>
            </div>
          </div>

          <Stack spacing={2}>
            <TextField
              select
              label="What to schedule"
              value={sensor}
              onChange={(e) => setSensor(e.target.value)}
              fullWidth
            >
              {SENSOR_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Start date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
              />
              <TextField
                label="End date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
              />
            </Stack>

            <TextField
              label="Trigger time"
              type="time"
              InputLabelProps={{ shrink: true }}
              value={time}
              onChange={(e) => setTime(e.target.value)}
              fullWidth
            />

            {/* Day of Week Selection */}
            <div className="section-label">Select days of the week</div>
            <div className="day-chip-grid">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                (day, index) => (
                  <Chip
                    key={day}
                    label={day}
                    clickable
                    aria-pressed={daysOfWeek[index]}
                    color={daysOfWeek[index] ? "primary" : "default"}
                    variant={daysOfWeek[index] ? "filled" : "outlined"}
                    onClick={() =>
                      setDaysOfWeek((prev) => {
                        const newDays = [...prev];
                        newDays[index] = !newDays[index];
                        return newDays;
                      })
                    }
                    className={daysOfWeek[index] ? "day-chip active" : "day-chip"}
                  />
                )
              )}
            </div>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={busy}
                fullWidth
              >
                {selectedId ? "Update schedule" : "Save schedule"}
              </Button>
              <Button variant="outlined" onClick={resetForm} disabled={busy} fullWidth>
                Clear
              </Button>
              {selectedId && (
                <Button
                  variant="outlined" color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  disabled={busy}
                  fullWidth
                >
                  Delete
                </Button>
              )}
            </Stack>
          </Stack>
        </div>

        {/* Existing schedules */}
        {showExisting && (
          <div className="scheduler-card list-card">
            <div className="card-head">
              <div className="icon-chip hollow">
                <VisibilityIcon />
              </div>
              <div>
                <h4>Saved schedules</h4>
                <p className="muted">Tap a schedule to load and edit the details.</p>
              </div>
            </div>
            {schedules.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No schedules yet.
              </Typography>
            ) : (
              <List dense className="schedule-list">
                {schedules.map((item) => {
                  const sensorLabel =
                    SENSOR_OPTIONS.find((s) => s.id === Number(item.sensor))
                      ?.label ?? item.sensor;
                  const daysLabel = mapDaysToLabels(item.daysOfWeek || []);

                  return (
                    <ListItemButton
                      key={item.id}
                      selected={selectedId === item.id}
                      onClick={() => loadForEdit(item)}
                      className="schedule-row"
                    >
                      <ListItemText
                        primary={
                          <div className="schedule-title">
                            <span>{sensorLabel}</span>
                            <Tooltip title="Start to end window">
                              <Chip
                                size="small"
                                label={`${item.startDate} → ${item.endDate}`}
                                className="ghost-chip"
                              />
                            </Tooltip>
                          </div>
                        }
                        secondary={
                          <div className="schedule-meta">
                            <span>
                              <ScheduleIcon fontSize="small" /> {item.time}
                            </span>
                            <span>
                              <EventRepeatIcon fontSize="small" /> {daysLabel || "One-time"}
                            </span>
                          </div>
                        }
                      />
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          loadForEdit(item);
                        }}
                        className="row-icon"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={async (e) => {
                          e.stopPropagation();
                          setSelectedId(item.id);
                          await handleDelete();
                        }}
                        className="row-icon"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SchedulerComponent;
